// Staff nameplate colors for the OR board.
//
// Each staffed room shows the DOCTOR in a big colored box and, to its right, the
// CRNA in a smaller nameplate. Colors are chosen automatically from each person's
// shift (see the two functions below). To recolor, edit NAMEPLATE_COLORS — it is
// the single source of truth for the palette.

import type { CSSProperties } from 'react';
import { utcToLocalTime } from '../utils/timezone.ts';

// ---- The editable palette (SOLID fills, white text) --------------------------
export const NAMEPLATE_COLORS = {
  // CRNAs — by shift end (green family):
  crnaOut3pm:     '#34c38f', // mint green — shift ends 3pm
  crnaOut5pm:     '#12795a', // dark lake green — shift ends 5pm
  crnaOut7pm:     '#9ac53a', // chartreuse — ends 7pm OR the 3pm–11pm evening shift
  crnaOvernight:  '#6b7785', // gray — the 11pm–7am overnight shift
  crnaDefault:    '#7a8a99', // fallback when no shift is known

  // Doctors — blue family, with two accents:
  docSupervise:   '#4a9fd4', // light blue (--or-blue-mid) — supervising CRNA(s)
  docSolo:        '#c25d1e', // burnt orange — alone in a room, no CRNA
  docReliefNeeded:'#b8860b', // deep golden yellow — shift-worker out at 3pm/5pm (needs relief); distinct from burnt-orange solo, readable with white text
  docDefault:     '#1a7abf', // fallback

  // Students — by competency:
  studentNew:          '#d92d20', // red — new, cannot function independently
  studentIntermediate: '#d9a406', // yellow — intermediate
  studentGreen:        '#1f9d55', // green — can run solo at 2:1 supervision
  studentDefault:      '#7a8a99', // competency not set

  request:        '#a020f0', // electric purple — "request" placement (click-to-recolor); stands out on blue/orange
  multiStart:     '#9333ea', // vivid purple — team covering 2+ non-overlapping early (<09:30) starts (Rule 8 reuse)

  text: '#ffffff',
};

// A room's later cases with no explicit staffing are assumed covered by the team from the case
// before them, shown in this green (an "assumed continuation" cue).
export const INHERITED_TEAM_COLOR = '#2e9e5b';

// Manual per-placement recolor options (click a name -> pick one). Only the colors we actually use
// on the board are offered, and each swatch's hover label mirrors the Color Legend under the bullpen
// so the meaning is identical in both places. Reset removes the override.
export const RECOLOR_OPTIONS: { label: string; color: string }[] = [
  // CRNA — by shift end
  { label: 'CRNA — Out 3pm', color: NAMEPLATE_COLORS.crnaOut3pm },
  { label: 'CRNA — Out 5pm', color: NAMEPLATE_COLORS.crnaOut5pm },
  { label: 'CRNA — Out 7pm / 3–11pm', color: NAMEPLATE_COLORS.crnaOut7pm },
  { label: 'CRNA — 11pm–7am', color: NAMEPLATE_COLORS.crnaOvernight },
  // Anesthesiologist
  { label: 'Anesthesiologist — Supervising CRNA(s)', color: NAMEPLATE_COLORS.docSupervise },
  { label: 'Anesthesiologist — Solo in a room', color: NAMEPLATE_COLORS.docSolo },
  { label: 'Anesthesiologist — Out 3pm/5pm, needs relief', color: NAMEPLATE_COLORS.docReliefNeeded },
  // Student — by competency
  { label: 'Student — New (no solo)', color: NAMEPLATE_COLORS.studentNew },
  { label: 'Student — Intermediate', color: NAMEPLATE_COLORS.studentIntermediate },
  { label: 'Student — Green (can solo 2:1)', color: NAMEPLATE_COLORS.studentGreen },
  // Other
  { label: 'Request case', color: NAMEPLATE_COLORS.request },
];

// Recolor swatches offered on a person's card, scoped to their ROLE (a doctor only gets
// doctor-meaningful colors, a CRNA only CRNA colors, a student only competency colors).
export function recolorOptionsFor(employeeType?: string): { label: string; color: string }[] {
  if (employeeType === 'doctor') return [
    { label: 'Supervising CRNA(s)', color: NAMEPLATE_COLORS.docSupervise },
    { label: 'Solo in a room', color: NAMEPLATE_COLORS.docSolo },
    { label: 'Out 3pm/5pm — needs relief', color: NAMEPLATE_COLORS.docReliefNeeded },
    { label: 'Request case', color: NAMEPLATE_COLORS.request },
  ];
  if (employeeType && employeeType.startsWith('nurse-student')) return [
    { label: 'New student', color: NAMEPLATE_COLORS.studentNew },
    { label: 'Intermediate student', color: NAMEPLATE_COLORS.studentIntermediate },
    { label: 'Green (senior) student', color: NAMEPLATE_COLORS.studentGreen },
  ];
  // CRNA (nurse)
  return [
    { label: 'Out 3pm', color: NAMEPLATE_COLORS.crnaOut3pm },
    { label: 'Out 5pm', color: NAMEPLATE_COLORS.crnaOut5pm },
    { label: 'Out 7pm / 3–11pm', color: NAMEPLATE_COLORS.crnaOut7pm },
    { label: '11pm–7am', color: NAMEPLATE_COLORS.crnaOvernight },
    { label: 'Request case', color: NAMEPLATE_COLORS.request },
  ];
}

// Student nameplate/brick color by competency level (slice 2).
export function studentNameplateColor(level?: string): string {
  switch (level) {
    case 'new': return NAMEPLATE_COLORS.studentNew;
    case 'intermediate': return NAMEPLATE_COLORS.studentIntermediate;
    case 'green': return NAMEPLATE_COLORS.studentGreen;
    default: return NAMEPLATE_COLORS.studentDefault;
  }
}

// A senior student IS the green tier in this app (green = "can run solo at 2:1 supervision"), so a
// 'nurse-student-senior' always reads GREEN even when its competency field was never set (the common case).
// Any other student type falls back to its explicit competency level.
export function studentColorFor(employeeType?: string, competency?: string): string {
  if (employeeType === 'nurse-student-senior') return NAMEPLATE_COLORS.studentGreen;
  return studentNameplateColor(competency);
}

// Rule 6: rooms whose case needs a nerve block get a yellow border. Keyword list mirrors the
// backend Symphony detector (extend as real case data arrives). This is the AUTO detection; a case
// can also be hand-flagged on the board (see caseMarkers.ts), which overrides the keyword result.
export const NERVE_BLOCK_BORDER = '#f2c94c'; // yellow
const NERVE_BLOCK_KEYWORDS = ['shoulder', 'knee'];
export function needsNerveBlock(surgeryType?: string): boolean {
  if (!surgeryType) return false;
  const lower = surgeryType.toLowerCase();
  return NERVE_BLOCK_KEYWORDS.some(k => lower.includes(k));
}

// Pediatric cases (age ≤ 12) get a hot-pink border. We deliberately do not collect or persist
// patient age from customer data (see backend/src/utils/jsonp-parser.ts), so this is MANUAL-ONLY:
// a case is flagged pediatric by hand on the board (see caseMarkers.ts), never auto-detected. A case
// can carry BOTH borders: a yellow nerve-block border and a pink pediatric border at the same time.
export const PEDIATRIC_BORDER = '#ff2d9b'; // hot pink
export const PEDIATRIC_AGE_MAX = 12;

// Minimal shift shape we read (matches the board's shift objects).
export interface NameplateShift {
  startTimeUtc?: string;
  endTimeUtc?: string;
  order?: number | null; // doctor pecking order; absent/null => shift worker
}

// Local hour (0–23) of a UTC timestamp, in the viewer's timezone (same basis the board uses).
function localHourOf(utc?: string): number {
  if (!utc) return NaN;
  const h = parseInt(utcToLocalTime(utc).slice(0, 2), 10);
  return h === 24 ? 0 : h;
}

// CRNA fill color from their shift (Rule 6 palette).
export function crnaNameplateColor(shift?: NameplateShift | null): string {
  if (!shift || !shift.endTimeUtc) return NAMEPLATE_COLORS.crnaDefault;
  const startH = localHourOf(shift.startTimeUtc);
  const endHRaw = localHourOf(shift.endTimeUtc);
  // A shift ending at midnight (00:00) is end-of-day, not 3am — treat hour 0 as 24 so it
  // doesn't fall into the "out by 3pm" bucket below.
  const endH = endHRaw === 0 ? 24 : endHRaw;
  if (!isNaN(startH) && (startH >= 22 || startH < 5)) return NAMEPLATE_COLORS.crnaOvernight; // 11pm–7am
  if (endH <= 15) return NAMEPLATE_COLORS.crnaOut3pm;  // until 3pm
  if (endH <= 17) return NAMEPLATE_COLORS.crnaOut5pm;  // until 5pm
  return NAMEPLATE_COLORS.crnaOut7pm;                   // 7pm, or the 3pm–11pm evening shift
}

// Doctor fill color: relief alarm takes precedence, then supervising vs. alone.
export function doctorNameplateColor(shift: NameplateShift | null | undefined, supervisedCrnaCount: number): string {
  const endH = shift ? localHourOf(shift.endTimeUtc) : NaN;
  const isShiftWorker = !shift || shift.order === undefined || shift.order === null;
  if (isShiftWorker && (endH === 15 || endH === 17)) return NAMEPLATE_COLORS.docReliefNeeded; // out at 3pm/5pm
  if (supervisedCrnaCount >= 1) return NAMEPLATE_COLORS.docSupervise;                          // supervising CRNA(s)
  return NAMEPLATE_COLORS.docSolo;                                                             // alone in a room
}

// Doctor color for the bullpen roster (before placement, so there's no supervised count yet):
// alarm red-orange if a shift-worker must leave at 3pm/5pm, otherwise the light-blue baseline.
export function doctorBullpenColor(shift?: NameplateShift | null): string {
  const endH = shift ? localHourOf(shift.endTimeUtc) : NaN;
  const isShiftWorker = !shift || shift.order === undefined || shift.order === null;
  if (isShiftWorker && (endH === 15 || endH === 17)) return NAMEPLATE_COLORS.docReliefNeeded;
  return NAMEPLATE_COLORS.docSupervise;
}

// Box styling — the doctor box is deliberately bigger/bolder than the CRNA plate.
export function doctorPlateStyle(background: string): CSSProperties {
  return {
    background, color: NAMEPLATE_COLORS.text, fontWeight: 700, fontSize: '0.82em',
    padding: '3px 9px', borderRadius: '6px', whiteSpace: 'nowrap', lineHeight: 1.25,
    letterSpacing: '0.2px', boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
  };
}
export function crnaPlateStyle(background: string): CSSProperties {
  return {
    background, color: NAMEPLATE_COLORS.text, fontWeight: 600, fontSize: '0.76em',
    padding: '2px 7px', borderRadius: '6px', whiteSpace: 'nowrap', lineHeight: 1.25,
  };
}
